import React from 'react';
import styled, { css } from 'styled-components';
import { Heading4, P } from '@ovotech/element-v5';
import { useBeyondAccountFlags } from '@/src/hooks/useBeyondAccountFlags/useBeyondAccountFlags';
import { breakpoints } from '@/src/utils/mediaQuery';
import { FilledSquare } from './FilledSquare';
import { EmptySquare } from './EmptySquare';

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: 0 auto;
  padding-top: 40px;
`;

const StepsContainer = styled.div`
  ${() => css`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;

    @media ${breakpoints.smallDown} {
      flex-direction: column;
      align-items: flex-start;
      gap: 32px;
    }
  `}
`;

const StepColumn = styled.div`
  ${() => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    position: relative;

    @media ${breakpoints.smallDown} {
      flex-direction: row;
      align-items: center;
      width: 100%;
    }
  `}
`;

const SquareContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  width: 65px;
  height: 65px;
  flex-shrink: 0;
`;

const SquareWrapper = styled.div`
  display: flex;
`;

const DashedLine = styled.div`
  ${() => css`
    position: absolute;
    top: 32px;
    left: 50%;
    width: 100%;
    height: 2px;
    z-index: 0;

    background-image: repeating-linear-gradient(
      to right,
      #044d29 0,
      #044d29 8px,
      transparent 8px,
      transparent 20px
    );

    @media ${breakpoints.smallDown} {
      width: 2px;
      height: calc(100% + 32px);

      left: 32.5px;
      top: 32.5px;
      transform: translateX(-50%);

      background-image: repeating-linear-gradient(
        to bottom,
        #044d29 0,
        #044d29 8px,
        transparent 8px,
        transparent 20px
      );
    }
  `}
`;

const StepContentWrapper = styled.div`
  ${() => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    margin-top: 24px;

    @media ${breakpoints.smallDown} {
      align-items: flex-start;
      margin-top: 0;
      margin-left: 24px;
    }
  `}
`;

const StepLabel = styled(Heading4)`
  ${({ theme }) => css`
    color: ${theme?.colors?.brand?.midnight || '#000'};
    text-align: center;
    margin: 0;

    @media ${breakpoints.smallDown} {
      text-align: left;
    }
  `}
`;

const StepDescription = styled(P)`
  ${() => css`
    color: #666;
    text-align: center;
    font-size: 16px;
    margin: 0;

    @media ${breakpoints.smallDown} {
      text-align: left;
    }
  `}
`;
export const BeyondProgress = () => {
  const { hasSmartMeter, hasDirectDebit, isActivated } =
    useBeyondAccountFlags();

  const smartMeterStep = {
    isDone: isActivated || hasSmartMeter,
    title: 'Smart meter',
    bodyCopy: isActivated || hasSmartMeter ? 'Connected' : 'Book now',
  };
  const directDebitStep = {
    isDone: isActivated || hasDirectDebit,
    title: 'Direct Debit',
    bodyCopy:
      isActivated || hasDirectDebit ? 'Active' : 'Spread your costs evenly',
  };
  const ovoAppStep = {
    isDone: isActivated,
    title: hasSmartMeter && hasDirectDebit ? 'Unlock your rewards' : 'OVO app',
    bodyCopy:
      hasSmartMeter && hasDirectDebit
        ? 'Download the OVO app'
        : 'Download it free',
  };

  const steps =
    hasDirectDebit && !hasSmartMeter
      ? [directDebitStep, smartMeterStep, ovoAppStep]
      : [smartMeterStep, directDebitStep, ovoAppStep];

  return (
    <ProgressContainer>
      <StepsContainer data-testid="steps-container">
        {steps.map((step, index) => (
          <StepColumn key={index} data-testid={`step-column-${index}`}>
            <SquareContainer data-testid={`square-container-${index}`}>
              <SquareWrapper data-testid="square-wrapper">
                {step.isDone ? (
                  <FilledSquare />
                ) : (
                  <EmptySquare number={index + 1} />
                )}
              </SquareWrapper>
            </SquareContainer>

            {index < steps.length - 1 && (
              <DashedLine data-testid={`dashed-line-${index}`} />
            )}

            <StepContentWrapper data-testid={`step-content-wrapper-${index}`}>
              <StepLabel data-testid="step-label">{step.title}</StepLabel>
              <StepDescription>{step.bodyCopy}</StepDescription>
            </StepContentWrapper>
          </StepColumn>
        ))}
      </StepsContainer>
    </ProgressContainer>
  );
};
